import type {
  AdminAiCostsResponse,
  AdminGeoActivityResponse,
  AdminGrowthOpportunitiesResponse,
  AdminLiveRoomGuestStatsResponse,
  AdminPlanStatsResponse,
  AdminRecentSignupsResponse,
  AdminUserStatsResponse,
  AnalyticsGranularity,
  BusinessMetricsResponse,
  BusinessOverviewResponse,
  EventAnalyticsResponse,
  EventSummaryResponse,
  GeoZoomTier,
  ListingAnalyticsResponse,
  PublicStatsResponse,
} from '@pulsify/shared/dto'
import { api, unwrap } from '~/shared/lib/rpc'

type BusinessMetrics = BusinessMetricsResponse
type ListingAnalytics = ListingAnalyticsResponse
type EventAnalytics = EventAnalyticsResponse

export interface AnalyticsParams {
  from?: string
  to?: string
  granularity: AnalyticsGranularity
}

function analyticsQuery(params: AnalyticsParams) {
  return params.granularity === 'all'
    ? { granularity: 'all' as const }
    : { from: params.from, to: params.to, granularity: params.granularity }
}

export const statsApi = {
  async getAdminRecentSignups(params: {
    days: number
    limit: number
  }): Promise<AdminRecentSignupsResponse> {
    const res = await api.api.admin.signups.recent.$get({
      query: { days: String(params.days), limit: String(params.limit) },
    })
    return unwrap<AdminRecentSignupsResponse>(res)
  },

  async getAdminGrowthOpportunities(params: {
    days: number
  }): Promise<AdminGrowthOpportunitiesResponse> {
    const res = await api.api.admin.growth.opportunities.$get({
      query: { days: String(params.days) },
    })
    return unwrap<AdminGrowthOpportunitiesResponse>(res)
  },

  async getAdminGeoActivity(params: {
    tier: GeoZoomTier
    days: number
  }): Promise<AdminGeoActivityResponse> {
    const res = await api.api.admin.geo.activity.$get({
      query: { tier: params.tier, days: String(params.days) },
    })
    return unwrap<AdminGeoActivityResponse>(res)
  },

  async getBusinessMetrics(businessId: string): Promise<BusinessMetrics> {
    const res = await api.api.stats.businesses[':businessId'].metrics.$get({
      param: { businessId },
    })
    return unwrap<BusinessMetrics>(res)
  },

  async getBusinessOverview(businessId: string): Promise<BusinessOverviewResponse> {
    const res = await api.api.stats.businesses[':businessId'].overview.$get({
      param: { businessId },
    })
    return unwrap<BusinessOverviewResponse>(res)
  },

  async getListingAnalytics(
    businessId: string,
    listingId: string,
    params: AnalyticsParams,
  ): Promise<ListingAnalytics> {
    const res = await api.api.stats.businesses[':businessId'].listings[':listingId'].analytics.$get(
      {
        param: { businessId, listingId },
        query: analyticsQuery(params),
      },
    )
    return unwrap<ListingAnalytics>(res)
  },

  async getEventAnalytics(
    businessId: string,
    eventId: string,
    params: AnalyticsParams,
  ): Promise<EventAnalytics> {
    const res = await api.api.stats.businesses[':businessId'].events[':eventId'].analytics.$get({
      param: { businessId, eventId },
      query: analyticsQuery(params),
    })
    return unwrap<EventAnalytics>(res)
  },

  async getEventSummary(businessId: string, eventId: string): Promise<EventSummaryResponse> {
    const res = await api.api.stats.businesses[':businessId'].events[':eventId'].summary.$get({
      param: { businessId, eventId },
    })
    return unwrap<EventSummaryResponse>(res)
  },

  async getPublicStats(): Promise<PublicStatsResponse> {
    const res = await api.api.stats.public.$get()
    return unwrap<PublicStatsResponse>(res)
  },

  async getAdminUserStats(): Promise<AdminUserStatsResponse> {
    const res = await api.api.admin.stats.users.$get()
    return unwrap<AdminUserStatsResponse>(res)
  },

  async getAdminPlanStats(): Promise<AdminPlanStatsResponse> {
    const res = await api.api.admin.stats.plans.$get()
    return unwrap<AdminPlanStatsResponse>(res)
  },

  async getAdminAiCosts(): Promise<AdminAiCostsResponse> {
    const res = await api.api.admin.stats['ai-costs'].$get()
    return unwrap<AdminAiCostsResponse>(res)
  },

  async getAdminLiveRoomGuestStats(): Promise<AdminLiveRoomGuestStatsResponse> {
    const res = await api.api.admin.stats['live-room-guests'].$get()
    return unwrap<AdminLiveRoomGuestStatsResponse>(res)
  },
}
